import { useEffect, useRef } from 'react'
import { getNearestCanvasEntityElement } from '@sketch/inspector'
import { useCanvasSelection } from '@sketch/canvas'

/**
 * On leaving the Inspect tab, collapse the selection to something view mode can
 * represent.
 *
 * View mode can only show entities - top-level frames and Sections - so a
 * selected sub-layer becomes its nearest ancestor entity, and is dropped when
 * it has none. An entity is already representable and is left alone.
 *
 * This is what lets one store serve both modes: every inspector selection has
 * an entity it maps onto, so there is no state one mode can hold and the other
 * cannot.
 */
export function useCollapseSelectionOnDismiss(isInspectorActive: boolean) {
  const { selectedElement, selectElement } = useCanvasSelection()
  const wasInspectorActive = useRef(isInspectorActive)

  useEffect(() => {
    const isDismissing = wasInspectorActive.current && !isInspectorActive
    wasInspectorActive.current = isInspectorActive

    if (!isDismissing || !selectedElement) return

    const entity = getNearestCanvasEntityElement(selectedElement)

    // Already an entity, so there is nothing to collapse. Writing it back would
    // store an equal-but-new selection, which React cannot bail out of.
    if (entity === selectedElement) return

    selectElement(entity ?? null)
  }, [isInspectorActive, selectedElement, selectElement])
}
